/**
 * WatchlistSuggestionCard — single Astor suggestion tile in the borrower
 * Watchlist's "Things you might want to watch" panel.
 *
 * Dashed-border button (mint-on-hover) with icon + title + sub + "+ Add" chip
 * CTA. Mocked client-side until the suggestion endpoint lands.
 *
 * TODO(astor suggestions API): swap the parent page's hardcoded suggestion
 * list for a real endpoint (e.g. `astor.watchlist.suggestions.useQuery`).
 * Schema needs to surface kind, title, subtitle, deep link / pin payload, and
 * a stable id for "dismiss" so we don't re-suggest after the user adds.
 */

'use client';

import {
  Box,
  baseColors,
  GlassChip,
  iconSizes,
  type SvgIconProps,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import type { ComponentType, ReactElement } from 'react';

export interface WatchlistSuggestionCardProps {
  icon: ComponentType<SvgIconProps>;
  title: string;
  subtitle: string;
  onAdd: () => void;
  disabled?: boolean;
}

export function WatchlistSuggestionCard({
  icon: Icon,
  title,
  subtitle,
  onAdd,
  disabled,
}: WatchlistSuggestionCardProps): ReactElement {
  const { t } = useI18n();

  return (
    <Box
      component="button"
      type="button"
      onClick={onAdd}
      disabled={disabled}
      sx={{
        all: 'unset',
        boxSizing: 'border-box',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1.75,
        minHeight: 120,
        width: '100%',
        borderRadius: 1.5,
        border: `1px dashed ${baseColors.teal[300]}`,
        bgcolor: 'background.paper',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        opacity: disabled ? 0.55 : 1,
        '&:hover:not(:disabled)': {
          borderColor: baseColors.teal[400],
          bgcolor: 'action.hover',
        },
        '&:focus-visible': {
          outline: `2px solid ${baseColors.teal[400]}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${baseColors.teal[400]}26`,
            color: baseColors.teal[400],
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: iconSizes.md }} />
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', flex: 1, textAlign: 'left' }}>
        {subtitle}
      </Typography>
      {/* R7: dot + label pattern matches the design's mint-dot chip
          convention (used everywhere else in the borrower portal for "live"
          / additive affordances) instead of a generic filled success pill. */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5 }}>
        <GlassChip
          icon={
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'success.main',
              }}
            />
          }
          label={t('consumer.watchlist.suggestionAdd')}
          size="small"
          color="success"
          variant="outlined"
          sx={{
            '& .MuiChip-label': { fontSize: '0.75rem', fontWeight: 500 },
            '& .MuiChip-icon': { ml: 1, mr: -0.5 },
          }}
        />
      </Box>
    </Box>
  );
}
